import React, { useEffect, useRef, useState } from 'react';
import { ApiDocs } from './ApiDocs.jsx';
import './Documentation.css';

const SECTIONS = [
  { id: 'priority', label: 'Platform Priority' },
  { id: 'philosophy', label: 'Core Philosophy' },
  { id: 'step-1', label: 'Step 1: Submission Intake' },
  { id: 'step-2', label: 'Step 2: Underwriting Canvas' },
  { id: 'step-3', label: 'Step 3: Scoring Framework' },
  { id: 'step-4', label: 'Step 4: Pricing & Rating' },
  { id: 'step-5', label: 'Step 5: Leadership Oversight' },
  { id: 'step-6', label: 'Step 6: Broker Experience' },
  { id: 'open-items', label: 'Open Items' },
];

export function Documentation() {
  const [hubTab, setHubTab] = useState('docs');
  const [activeId, setActiveId] = useState(SECTIONS[0].id);
  const sectionRefs = useRef({});

  useEffect(() => {
    const observers = [];

    SECTIONS.forEach(({ id }) => {
      const el = sectionRefs.current[id];
      if (!el) return;

      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setActiveId(id);
          }
        },
        { rootMargin: '-20% 0px -60% 0px', threshold: 0 }
      );

      observer.observe(el);
      observers.push(observer);
    });

    return () => observers.forEach((o) => o.disconnect());
  }, []);

  const ref = (id) => (el) => {
    sectionRefs.current[id] = el;
  };

  return (
    <div>
      {/* Hub Tab Bar */}
      <div className="hub-tabs">
        <button className={`hub-tab ${hubTab === 'docs' ? 'hub-tab--active' : ''}`} onClick={() => setHubTab('docs')}>Documentation</button>
        <button className={`hub-tab ${hubTab === 'api' ? 'hub-tab--active' : ''}`} onClick={() => setHubTab('api')}>API Reference</button>
      </div>

      {hubTab === 'api' && <ApiDocs />}
      {hubTab === 'docs' && (
    <div className="docs">
      {/* Sticky sidebar TOC */}
      <nav className="docs__toc" aria-label="Table of contents">
        <div className="docs__toc-inner">
          <p className="docs__toc-title">Contents</p>
          <ul className="docs__toc-list">
            {SECTIONS.map((s) => (
              <li key={s.id}>
                <a
                  href={`#${s.id}`}
                  className={`docs__toc-link${activeId === s.id ? ' docs__toc-link--active' : ''}`}
                  onClick={(e) => {
                    e.preventDefault();
                    sectionRefs.current[s.id]?.scrollIntoView({ behavior: 'smooth' });
                  }}
                >
                  {s.label}
                </a>
              </li>
            ))}
          </ul>
        </div>
      </nav>

      {/* Main content */}
      <div className="docs__content">
        {/* Hero */}
        <div className="docs__hero card">
          <p className="docs__hero-eyebrow">Platform Documentation</p>
          <h1 className="docs__hero-title">DEX Engine</h1>
          <p className="docs__hero-subtitle">
            An AI-native underwriting operating system for MGAs. This guide describes
            the platform as built — from document intake through scoring, pricing,
            and broker delivery.
          </p>
        </div>

        {/* Platform Priority */}
        <section className="docs__section" id="priority" ref={ref('priority')}>
          <div className="docs__section-card">
            <div className="docs__section-header">
              <h2 className="docs__section-title">Platform Priority</h2>
            </div>
            <p className="docs__section-desc">
              The DEX Engine is optimized in the following order. All workflows,
              data structures, and interfaces reflect this ordering.
            </p>
            <ol className="docs__priority-list">
              <li>
                <strong>Underwriters and their ideas</strong> — the platform exists
                to capture, structure, and scale human underwriting judgment.
              </li>
              <li>
                <strong>MGA leadership and capacity providers</strong> — oversight,
                portfolio visibility, and predictable behavior without friction.
              </li>
              <li>
                <strong>Brokers, both wholesale and retail</strong> — decisive
                answers, clear rationale, and clean quotes.
              </li>
            </ol>
          </div>
        </section>

        {/* Core Philosophy */}
        <section className="docs__section" id="philosophy" ref={ref('philosophy')}>
          <div className="docs__section-card">
            <div className="docs__section-header">
              <h2 className="docs__section-title">Core Philosophy</h2>
            </div>
            <p className="docs__section-desc">
              DEX Engine is an AI-native, underwriting-first platform designed to
              capture, structure, and scale human judgment. The Engine is the single
              operating system for the MGA — not a tool bolted onto existing
              workflows, but the workflow itself.
            </p>
            <p className="docs__section-desc">
              AI is embedded at every layer: document extraction, risk tagging,
              score calibration, pricing indication, and underwriter assistance. The
              models are trained on senior underwriter decisions, not generic
              insurance data.
            </p>
            <p className="docs__section-desc">
              Over time, the Engine will handle underwriting, distribution
              management, policy administration, accounting and finance, and claims.
              At this stage, the focus is deliberately on underwriting flow,
              leadership oversight, and broker interaction.
            </p>
            <div className="docs__principle">
              The system does not replace underwriters. It prepares risks, enforces
              discipline, and creates visibility so strong underwriting judgment can
              scale.
            </div>
          </div>
        </section>

        {/* Step 1: Submission Intake */}
        <section className="docs__section" id="step-1" ref={ref('step-1')}>
          <div className="docs__section-card">
            <div className="docs__section-header">
              <span className="docs__section-step">1</span>
              <h2 className="docs__section-title">Submission Intake</h2>
            </div>
            <p className="docs__section-desc">
              Submissions are treated as underwriting ideas that need structure, not
              static forms. Today, 95% of broker submissions still arrive via email —
              the portal serves as a status tracker and resource center while the
              Engine handles the heavy lifting behind the scenes.
            </p>

            <p className="docs__section-desc" style={{ fontWeight: 600 }}>
              Document upload and extraction:
            </p>
            <ul className="docs__list">
              <li>
                Drag-and-drop upload supporting PDF, DOCX, XLSX, CSV, and image
                files
              </li>
              <li>
                Automatic carrier application recognition across 6 formats: TMHCC
                RSA, TMHCC TechGuard, CFC Tech, CFC Cyber, Corvus Cyber, and Corvus
                Tech E&amp;O
              </li>
              <li>
                Text extraction via AWS Textract for scanned PDFs and images
              </li>
              <li>
                AI-powered structured data extraction maps carrier-specific
                questions to a standard field set across 1,000+ NAICS codes
              </li>
              <li>
                Supplemental document processing for loss runs, SOC2 reports, and
                financial statements
              </li>
            </ul>

            <p className="docs__section-desc" style={{ fontWeight: 600 }}>
              Routing and ownership:
            </p>
            <ul className="docs__list">
              <li>
                Each submission generates a unique account page under the submitting
                broker
              </li>
              <li>
                Submissions are routed to underwriters based on real-time workload
                capacity
              </li>
              <li>
                Broker ownership is driven by submission, not by BOR or AOR
              </li>
              <li>
                Missing data is flagged but does not block underwriting visibility —
                underwriters can engage with imperfect submissions early
              </li>
            </ul>
            <div className="docs__principle">
              Key principle: &ldquo;Underwriters can see imperfect risks early.&rdquo;
            </div>
          </div>
        </section>

        {/* Step 2: Underwriting Canvas */}
        <section className="docs__section" id="step-2" ref={ref('step-2')}>
          <div className="docs__section-card">
            <div className="docs__section-header">
              <span className="docs__section-step">2</span>
              <h2 className="docs__section-title">Underwriting Canvas</h2>
            </div>
            <p className="docs__section-desc">
              The canvas is where underwriting happens. Before an underwriter
              engages, the Engine frames the risk with a score, tags, and organized
              submission data. The underwriter reviews, adjusts, and decides.
            </p>

            <p className="docs__section-desc" style={{ fontWeight: 600 }}>
              Risk scoring:
            </p>
            <div className="docs__risk-tiers">
              <span className="badge badge--green">1 – 6 Low Hazard</span>
              <span className="badge badge--orange">7 – 13 Moderate Hazard</span>
              <span className="badge badge--red">14 – 20 High Hazard</span>
            </div>
            <ul className="docs__list">
              <li>
                Risks scored on a 1–20 scale (higher = riskier)
              </li>
              <li>
                AI scoring calibrated from 57 senior underwriter training reviews
              </li>
              <li>
                Score auto-calculates from base industry hazard plus tag adjustments
              </li>
              <li>Auto-save with debounce on every change</li>
            </ul>

            <p className="docs__section-desc" style={{ fontWeight: 600 }}>
              40 risk tags:
            </p>
            <ul className="docs__list">
              <li>
                <strong>20 control tags</strong> — MFA, EDR, immutable backups,
                email filtering, patch management, incident response plan, and more
              </li>
              <li>
                <strong>20 exposure tags</strong> — PHI data, PCI scope, wire
                transfer exposure, critical infrastructure, high litigation
                jurisdiction, and more
              </li>
              <li>
                Auto-tagging: the Engine reads application data and pre-selects
                applicable tags before the underwriter opens the canvas
              </li>
            </ul>

            <p className="docs__section-desc" style={{ fontWeight: 600 }}>
              Submission data layout:
            </p>
            <ul className="docs__list">
              <li>
                10 collapsible sections: Company Info, MFA, Endpoint Security, Email
                Security, Security Governance, Backup &amp; Recovery, Data Exposure,
                Loss History, Wire Transfer Controls, Narrative Responses
              </li>
              <li>
                Data extracted from carrier applications is mapped into these
                sections automatically
              </li>
            </ul>

            <p className="docs__section-desc" style={{ fontWeight: 600 }}>
              Pricing indication:
            </p>
            <ul className="docs__list">
              <li>
                Suggested premium, retention, limit, and ransomware sublimit
                recommendation generated from scoring and account profile
              </li>
              <li>
                Benchmarks derived from underwriter training data and comparable
                accounts
              </li>
            </ul>

            <p className="docs__section-desc" style={{ fontWeight: 600 }}>
              AI chat assistant:
            </p>
            <ul className="docs__list">
              <li>
                Context-aware assistant with access to claims benchmarks, threat
                intelligence, and underwriter training data
              </li>
              <li>
                Available directly on the canvas to support underwriter decision-making
              </li>
            </ul>

            <div className="docs__principle">
              Key principle: &ldquo;The system frames the problem, not the answer.&rdquo;
            </div>
          </div>
        </section>

        {/* Step 3: Scoring Framework */}
        <section className="docs__section" id="step-3" ref={ref('step-3')}>
          <div className="docs__section-card">
            <div className="docs__section-header">
              <span className="docs__section-step">3</span>
              <h2 className="docs__section-title">Scoring Framework</h2>
            </div>
            <p className="docs__section-desc">
              The 1–20 scoring framework is the backbone of every underwriting
              decision in the Engine. It translates qualitative risk assessment into
              a consistent, auditable number.
            </p>

            <p className="docs__section-desc" style={{ fontWeight: 600 }}>
              How scoring works:
            </p>
            <ul className="docs__list">
              <li>
                Every account starts with a base industry hazard score derived from
                NAICS code and company profile
              </li>
              <li>
                Controls are scored relative to company size — a 50-person firm
                without EDR is weighted differently than a 5,000-person firm
              </li>
              <li>
                Industry class adjustments: law firms, utilities, education, and
                healthcare carry higher base hazard
              </li>
              <li>
                Calibration data from 57 senior underwriter reviews ensures the model
                reflects real judgment, not generic benchmarks
              </li>
            </ul>

            <p className="docs__section-desc" style={{ fontWeight: 600 }}>
              Tag impact examples:
            </p>
            <ul className="docs__list">
              <li>
                <span className="badge badge--red">NO_MFA_PRESENT</span>{' '}
                +5 to score
              </li>
              <li>
                <span className="badge badge--green">IMMUTABLE_BACKUPS</span>{' '}
                −4 from score
              </li>
              <li>
                <span className="badge badge--red">PHI_DATA</span>{' '}
                +4 to score
              </li>
              <li>
                <span className="badge badge--green">EDR_DEPLOYED</span>{' '}
                −3 from score
              </li>
              <li>
                <span className="badge badge--orange">WIRE_TRANSFER_EXPOSURE</span>{' '}
                +3 to score
              </li>
            </ul>

            <p className="docs__section-desc" style={{ fontWeight: 600 }}>
              Decision thresholds:
            </p>
            <div className="docs__risk-tiers">
              <span className="badge badge--green">1 – 6 Low Hazard</span>
              <span className="badge badge--orange">7 – 13 Moderate Hazard</span>
              <span className="badge badge--red">14 – 20 High Hazard</span>
            </div>
            <ul className="docs__list">
              <li>
                <strong>Low Hazard (1–6):</strong> Straightforward risks. Candidates
                for streamlined quoting and favorable terms.
              </li>
              <li>
                <strong>Moderate Hazard (7–13):</strong> Requires underwriter review.
                Pricing and terms adjusted based on specific tag profile.
              </li>
              <li>
                <strong>High Hazard (14–20):</strong> Elevated scrutiny. May require
                leadership review, restrictive sublimits, or declination depending on
                exposure concentration.
              </li>
            </ul>

            <div className="docs__principle">
              Key principle: &ldquo;Every score is explainable. Every adjustment is
              traceable.&rdquo;
            </div>
          </div>
        </section>

        {/* Step 4: Pricing & Rating */}
        <section className="docs__section" id="step-4" ref={ref('step-4')}>
          <div className="docs__section-card">
            <div className="docs__section-header">
              <span className="docs__section-step">4</span>
              <h2 className="docs__section-title">Pricing &amp; Rating</h2>
            </div>
            <p className="docs__section-desc">
              The Engine generates automated pricing indications based on the
              account profile, risk score, and underwriter training data.
            </p>

            <ul className="docs__list">
              <li>
                Pricing driven by revenue, industry classification, control posture,
                and requested limit
              </li>
              <li>
                Premium benchmarks calibrated from senior underwriter training data
              </li>
              <li>
                Retention scales with limit size and company revenue
              </li>
              <li>
                Ransomware sublimit recommendations for High Hazard risks
                (score 14–20) based on exposure tag profile
              </li>
              <li>
                Comparable account references surfaced to give underwriters pricing
                context from similar risks
              </li>
            </ul>

            <div className="docs__principle">
              Key principle: &ldquo;Pricing starts from data, not from scratch.&rdquo;
            </div>
          </div>
        </section>

        {/* Step 5: Leadership Oversight */}
        <section className="docs__section" id="step-5" ref={ref('step-5')}>
          <div className="docs__section-card">
            <div className="docs__section-header">
              <span className="docs__section-step">5</span>
              <h2 className="docs__section-title">Leadership Oversight</h2>
            </div>
            <p className="docs__section-desc">
              Leadership and capacity oversight is passive, not intrusive. The
              dashboard gives leaders real-time visibility without interrupting
              underwriter workflow.
            </p>

            <p className="docs__section-desc" style={{ fontWeight: 600 }}>
              Dashboard capabilities:
            </p>
            <ul className="docs__list">
              <li>
                Performance stats: month-to-date bound count, quoted count, and hit
                ratio
              </li>
              <li>
                Two-panel layout with a &ldquo;My Work&rdquo; panel for personal queue
                management
              </li>
              <li>
                View-as switcher allows leadership to see underwriter-specific views
              </li>
              <li>
                Review Summary page for tracking scoring decisions across the team
              </li>
              <li>
                Portfolio-level metrics for capacity providers
              </li>
            </ul>

            <ul className="docs__list">
              <li>
                Underwriters are not interrupted unless thresholds are crossed
              </li>
              <li>
                Capacity sees consistent structure, explainable decisions, and
                predictable portfolio behavior
              </li>
            </ul>

            <div className="docs__principle">
              Key principle: &ldquo;Oversight without friction.&rdquo;
            </div>
          </div>
        </section>

        {/* Step 6: Broker Experience */}
        <section className="docs__section" id="step-6" ref={ref('step-6')}>
          <div className="docs__section-card">
            <div className="docs__section-header">
              <span className="docs__section-step">6</span>
              <h2 className="docs__section-title">Broker Experience</h2>
            </div>
            <p className="docs__section-desc">
              The broker experience is intentionally minimal. Brokers see submission
              status and progress — not internal scoring, tags, or underwriting
              mechanics. By the time a broker hears back: the decision is decisive,
              the rationale is clear, the quote is clean, the next step is obvious.
            </p>
            <ul className="docs__list">
              <li>
                Brokers see quote progress via a color-coded status bar
              </li>
              <li>
                Limited tabs: form comparisons, marketing materials
              </li>
              <li>
                Internal scoring and risk tags are never exposed to the broker view
              </li>
            </ul>
            <div className="docs__principle">
              Key principle: &ldquo;Complexity stays inside the Engine.&rdquo;
            </div>
          </div>
        </section>

        {/* Open Items */}
        <section className="docs__section" id="open-items" ref={ref('open-items')}>
          <div className="docs__section-card">
            <div className="docs__section-header">
              <h2 className="docs__section-title">Open Items</h2>
            </div>
            <p className="docs__section-desc">
              The following areas are under active development or discussion and will
              be addressed in upcoming releases.
            </p>
            <ul className="docs__open-items">
              <li className="docs__open-item">
                UW Manager referral queue and approval workflow
              </li>
              <li className="docs__open-item">
                CUO portfolio dashboard with concentration risk visibility
              </li>
              <li className="docs__open-item">
                Finance AI dashboard
              </li>
              <li className="docs__open-item">
                Sales CRM
              </li>
              <li className="docs__open-item">
                Email ingestion for submissions
              </li>
              <li className="docs__open-item">
                PDF output — generate filled carrier applications from Engine data
              </li>
              <li className="docs__open-item">
                Claims training and loss pattern recognition
              </li>
            </ul>
          </div>
        </section>
      </div>
    </div>
      )}
    </div>
  );
}
